/**
 * Service pour la gestion du profil prestataire libre
 */

const API_URL = process.env.REACT_APP_API_URL || "";

/**
 * Obtenir le token d'authentification
 */
const getAuthHeader = (): HeadersInit => {
  const token = localStorage.getItem("token");
  return {
    Authorization: token ? `Bearer ${token}` : "",
    Accept: "application/json",
    "Content-Type": "application/json",
  };
};

/**
 * Gestion des erreurs API
 */
const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Erreur ${response.status}`);
  }
  return response.json();
};

// ============================================
// Types - Structure correspondant à l'API
// ============================================

/**
 * Entité (prestataire libre)
 */
export interface Entite {
  id: number;
  nom: string;
  siret: string;
  visible: boolean;
  progression: number;
  interesse_accord_cadre: boolean;
  logo_path?: string | null;
  logo_type?: string | null;
  logo_url?: string | null;
}

/**
 * Adresse structurée
 * FRONT-24: Structure enrichie retournée par l'API CORE-33
 */
export interface AdresseData {
  numero_voie?: string | null;
  type_voie?: string | null;
  nom_voie: string | null;
  code_postal: string | null;
  ville: string | null;
  departement?: string | null;
  pays: string | null;
}

/**
 * Entreprise
 * FRONT-24: Enrichi avec les nouveaux champs CORE-33
 */
export interface Entreprise {
  id: number;
  nom: string;
  siret: string | null; // FRONT-24: Ajout siret
  precision?: string | null; // FRONT-24: Précision du nom
  RCS: string | null;
  statut_juridique: string | null;
  capital: number | null;
  date_creation: number | null; // FRONT-24: Année uniquement (ex: 2015)
  redressement_judiciaire: boolean; // FRONT-24: Flag booléen
  adresse: AdresseData | null; // FRONT-24: Adresse structurée
}

/**
 * Présentation de l'entreprise
 */
export interface Presentation {
  id: number;
  presentation_entreprise: string | null;
  presentation_operationnel: string | null;
  presentation_humain: string | null;
  presentation_environnement: string | null;
  ca_n1: number | null;
  part_ca_n1: number | null;
  ca_n2: number | null;
  part_ca_n2: number | null;
  ca_n3: number | null;
  part_ca_n3: number | null;
  rh_n1: number | null;
  part_rh_n1: number | null;
  rh_n2: number | null;
  part_rh_n2: number | null;
  rh_n3: number | null;
  part_rh_n3: number | null;
}

/**
 * Coordonnées (interlocuteur administratif)
 */
export interface Coordonnees {
  id: number;
  legal_nom: string | null;
  legal_prenom: string | null;
  legal_fonction: string | null;
  legal_email: string | null;
  legal_tel: string | null;
  compta_nom: string | null;
  compta_prenom: string | null;
  compta_fonction: string | null;
  compta_email: string | null;
  compta_tel: string | null;
  facture_nom: string | null;
  facture_prenom: string | null;
  facture_fonction: string | null;
  facture_email: string | null;
  facture_tel: string | null;
  // FRONT-15: Acceptation conditions Centralis
  acceptation_conditions_centralis: boolean;
  acceptation_conditions_date: string | null;
}

/**
 * Coordonnées bancaires
 */
export interface Bancaire {
  id: number;
  nom_titulaire: string | null;
  banque: string | null;
  masked_iban: string | null;
  IBAN?: string | null; // IBAN complet (retourné en mode édition)
  BIC: string | null;
  numero_tva?: string | null; // FRONT-11: TVA intracommunautaire
}

/**
 * Lot technique
 * FRONT-09: Ajout de is_ac pour comptes AC
 */
export interface LotTechnique {
  id: number;
  nom: string;
  code: string;
  is_ac?: boolean; // FRONT-09: true si lot issu d'un Accord Cadre
}

/**
 * Département
 */
export interface Departement {
  id: number;
  nom: string;
  code: string | number;
}

/**
 * Association lot technique / département
 * FRONT-09: Ajout de rang et is_locked pour comptes AC
 */
export interface LotDepartement {
  id: number;
  lot_technique_id: number;
  lot_technique_nom: string;
  lot_technique_code: string;
  departement_id: number;
  departement_nom: string;
  departement_code: string;
  region_id?: number;
  region_nom?: string;
  rang?: number | null; // FRONT-09: Rang dans l'AC
  is_locked?: boolean; // FRONT-09: true si non modifiable
}

/**
 * Lots (structure complète)
 * FRONT-09: is_locked au niveau global = false permet d'ajouter des lots libres
 *           is_locked sur chaque lot_departement = true pour les lots AC titulaires
 */
export interface Lots {
  lots_techniques: LotTechnique[];
  departements: Departement[];
  lots_departements: LotDepartement[];
  is_locked?: boolean; // FRONT-09: false = peut ajouter des lots
}

/**
 * Prestataire libre
 */
export interface PrestataireLibre {
  id: number;
  interesse_accord_cadre: boolean;
  date_inscription: string;
  lots_departements: LotDepartement[];
}

/**
 * Champ de complétion
 */
export interface CompletionField {
  label: string;
  filled: boolean;
  required: boolean;
}

/**
 * Détail d'une section de complétion
 */
export interface SectionDetail {
  label: string;
  weight: number;
  fields: Record<string, CompletionField>;
}

/**
 * Élément manquant
 */
export interface MissingItem {
  section: string;
  section_label: string;
  field: string;
  label: string;
}

/**
 * Complétion du profil
 */
export interface ProfileCompletion {
  percent: number;
  sections: {
    presentation: boolean;
    documents: boolean;
    coordonnees: boolean;
    bancaire: boolean;
  };
  section_details: {
    presentation: SectionDetail;
    documents: SectionDetail;
    coordonnees: SectionDetail;
    bancaire: SectionDetail;
  };
  missing: MissingItem[];
  is_complete: boolean;
}

/**
 * Document uploadé
 */
export interface Document {
  id: number;
  type: string;
  type_label: string;
  key: string;
  commentaire: string | null;
  filename?: string;
  filesize?: number;
  mime_type?: string;
  created_at: string;
  updated_at?: string;
}

/**
 * Statut d'un type de document
 */
export interface DocumentStatus {
  type: string;
  label: string;
  required: boolean;
  uploaded: boolean;
}

/**
 * Données complètes du profil (structure API)
 */
export interface ProfileData {
  entite: Entite;
  entreprise: Entreprise | null;
  presentation: Presentation | null;
  coordonnees: Coordonnees | null;
  bancaire: Bancaire | null;
  documents: Document[];
  documents_status: DocumentStatus[];
  lots: Lots;
  prestataire_libre: PrestataireLibre | null;
  completion: ProfileCompletion;
}

// ============================================
// Types pour les mises à jour
// ============================================

/**
 * Données pour mise à jour de la présentation
 */
export interface PresentationData {
  presentation_entreprise?: string;
  presentation_operationnel?: string;
  presentation_humain?: string;
  presentation_environnement?: string;
  ca_n1?: number | null;
  part_ca_n1?: number | null;
  ca_n2?: number | null;
  part_ca_n2?: number | null;
  ca_n3?: number | null;
  part_ca_n3?: number | null;
  rh_n1?: number | null;
  part_rh_n1?: number | null;
  rh_n2?: number | null;
  part_rh_n2?: number | null;
  rh_n3?: number | null;
  part_rh_n3?: number | null;
}

/**
 * Données pour mise à jour des coordonnées
 * FRONT-15: compta_* et facture_email sont maintenant obligatoires
 * FRONT-15: Ajout acceptation_conditions_centralis
 */
export interface CoordonneesData {
  legal_nom: string;
  legal_prenom: string;
  legal_fonction?: string;
  legal_email: string;
  legal_tel: string;
  // FRONT-15: Contact comptabilité obligatoire
  compta_nom: string;
  compta_prenom: string;
  compta_fonction?: string;
  compta_email: string;
  compta_tel: string;
  // FRONT-15: Facturation = email seul obligatoire
  facture_email: string;
  // FRONT-15: Acceptation conditions obligatoire
  acceptation_conditions_centralis: boolean;
}

/**
 * Données pour mise à jour des coordonnées bancaires
 */
export interface BancaireData {
  nom_titulaire: string;
  banque: string;
  IBAN: string;
  BIC: string;
  numero_tva?: string; // FRONT-11: TVA intracommunautaire (optionnel)
}

/**
 * BUGFIX: Types de réponse partielles pour les mises à jour
 */
export interface UpdatePresentationResponse {
  presentation: Presentation | null;
  completion: ProfileCompletion;
}

export interface UpdateCoordonneesResponse {
  coordonnees: Coordonnees;
  completion: ProfileCompletion;
}

export interface UpdateBancaireResponse {
  bancaire: Bancaire;
  completion: ProfileCompletion;
}

/**
 * FRONT-24: Type de réponse pour mise à jour entreprise
 */
export interface UpdateEntrepriseResponse {
  entreprise: Entreprise;
  completion: ProfileCompletion;
}

/**
 * FRONT-24: Données pour mise à jour des informations entreprise
 * Exclut nom et siret (read-only)
 */
export interface EntrepriseFormData {
  precision?: string | null;
  RCS?: string | null;
  statut_juridique?: string | null;
  capital?: number | null;
  date_creation?: number | null;
  redressement_judiciaire?: boolean;
  adresse?: {
    numero_voie?: string | null;
    type_voie?: string | null;
    nom_voie?: string | null;
    code_postal?: string | null;
    ville?: string | null;
    pays?: string | null;
  };
}

// ============================================
// FRONT-14: Types pour les Références (mis à jour depuis FRONT-12)
// ============================================

/**
 * Référence projet (lecture)
 * FRONT-14: Ajout de is_complete depuis l'API CORE-23
 */
export interface Reference {
  numero: number;
  nom_operation: string | null;
  client: string | null;
  budget: number | null;
  annee_livraison: number | null;
  descriptif: string | null;
  has_photo: boolean;
  photo_filename: string | null;
  is_complete?: boolean; // FRONT-14: Calculé côté API
}

/**
 * Données pour mise à jour d'une référence
 */
export interface ReferenceData {
  numero: number;
  nom_operation?: string | null;
  client?: string | null;
  budget?: number | null;
  annee_livraison?: number | null;
  descriptif?: string | null;
}

// ============================================
// API Calls
// ============================================

/**
 * Récupère les données complètes du profil
 */
export const getProfile = async (): Promise<ProfileData> => {
  const response = await fetch(`${API_URL}/profile`, {
    method: "GET",
    headers: getAuthHeader(),
  });

  const result = await handleResponse(response);
  return result.data;
};

/**
 * Récupère uniquement les données de complétion
 */
export const getProfileCompletion = async (): Promise<ProfileCompletion> => {
  const response = await fetch(`${API_URL}/profile/completion`, {
    method: "GET",
    headers: getAuthHeader(),
  });

  const result = await handleResponse(response);
  return result.data;
};

/**
 * Met à jour la section présentation
 */
export const updatePresentation = async (
  data: PresentationData,
): Promise<UpdatePresentationResponse> => {
  const response = await fetch(`${API_URL}/profile/presentation`, {
    method: "PUT",
    headers: getAuthHeader(),
    body: JSON.stringify(data),
  });

  const result = await handleResponse(response);
  return result.data;
};

/**
 * Met à jour la section coordonnées
 * BUGFIX: Retourne les coordonnées mises à jour + completion (pas le profil complet)
 */
export const updateCoordonnees = async (
  data: CoordonneesData,
): Promise<UpdateCoordonneesResponse> => {
  const response = await fetch(`${API_URL}/profile/coordonnees`, {
    method: "PUT",
    headers: getAuthHeader(),
    body: JSON.stringify(data),
  });

  const result = await handleResponse(response);
  return result.data;
};

/**
 * Met à jour la section bancaire
 * BUGFIX: Retourne les données bancaires mises à jour + completion (pas le profil complet)
 */
export const updateBancaire = async (
  data: BancaireData,
): Promise<UpdateBancaireResponse> => {
  const response = await fetch(`${API_URL}/profile/bancaire`, {
    method: "PUT",
    headers: getAuthHeader(),
    body: JSON.stringify(data),
  });

  const result = await handleResponse(response);
  return result.data;
};

/**
 * FRONT-24: Met à jour les informations entreprise (sauf nom et siret)
 */
export const updateEntreprise = async (
  data: EntrepriseFormData,
): Promise<UpdateEntrepriseResponse> => {
  const response = await fetch(`${API_URL}/profile/entreprise`, {
    method: "PUT",
    headers: getAuthHeader(),
    body: JSON.stringify(data),
  });

  const result = await handleResponse(response);
  return result.data;
};

/**
 * Données pour mise à jour des lots (ancien format - rétrocompatibilité)
 */
export interface LotsData {
  lots_techniques: number[];
  departements: number[];
  regions?: number[];
}

/**
 * Données pour mise à jour des lots (nouveau format FRONT-04)
 */
export interface LotsUpdateData {
  lot_technique_id: number;
  departements: number[];
}

/**
 * Met à jour les lots techniques et géographiques (prestataire libre)
 * Ancien format - gardé pour rétrocompatibilité
 */
export const updateLots = async (data: LotsData): Promise<ProfileData> => {
  const response = await fetch(`${API_URL}/profile/lots`, {
    method: "PUT",
    headers: getAuthHeader(),
    body: JSON.stringify(data),
  });

  const result = await handleResponse(response);
  return result.data;
};

/**
 * FRONT-04: Met à jour les zones d'intervention pour un lot technique donné
 * @param lotTechniqueId ID du lot technique
 * @param departements IDs des départements sélectionnés
 */
export const updateLotsForLotTechnique = async (
  lotTechniqueId: number,
  departements: number[],
): Promise<ProfileData> => {
  const response = await fetch(`${API_URL}/profile/lots`, {
    method: "PUT",
    headers: getAuthHeader(),
    body: JSON.stringify({
      lot_technique_id: lotTechniqueId,
      departements: departements,
    }),
  });

  const result = await handleResponse(response);
  return result.data;
};

// ============================================
// FRONT-12: API Calls Références
// ============================================

/**
 * Récupère les 3 références de l'entité
 */
export const getReferences = async (): Promise<{ references: Reference[] }> => {
  const response = await fetch(`${API_URL}/profile/references`, {
    method: "GET",
    headers: getAuthHeader(),
  });
  const result = await handleResponse(response);
  return result.data;
};

/**
 * Met à jour les 3 références (bulk update)
 */
export const updateReferences = async (
  references: ReferenceData[],
): Promise<{ references: Reference[] }> => {
  const response = await fetch(`${API_URL}/profile/references`, {
    method: "PUT",
    headers: getAuthHeader(),
    body: JSON.stringify({ references }),
  });
  const result = await handleResponse(response);
  return result.data;
};

/**
 * Résultat de l'upload photo (FRONT-14/CORE-23)
 */
export interface PhotoUploadResult {
  success: boolean;
  message: string;
  data?: {
    numero: number;
    has_photo: boolean;
    filename: string;
    mime_type: string;
  };
}

/**
 * Upload photo pour une référence
 * FRONT-14: Retourne maintenant les données pour mise à jour locale
 */
export const uploadReferencePhoto = async (
  numero: number,
  file: File,
): Promise<PhotoUploadResult> => {
  const formData = new FormData();
  formData.append("photo", file);

  const token = localStorage.getItem("token");
  const response = await fetch(
    `${API_URL}/profile/references/${numero}/photo`,
    {
      method: "POST",
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        Accept: "application/json",
      },
      body: formData,
    },
  );

  return await handleResponse(response);
};

/**
 * Supprime la photo d'une référence
 */
export const deleteReferencePhoto = async (numero: number): Promise<void> => {
  const response = await fetch(
    `${API_URL}/profile/references/${numero}/photo`,
    {
      method: "DELETE",
      headers: getAuthHeader(),
    },
  );
  await handleResponse(response);
};

/**
 * Télécharge la photo d'une référence (avec authentification)
 * FRONT-14: Utilise fetch + blob pour passer le token Bearer
 */
export const downloadReferencePhoto = async (
  numero: number,
  filename: string,
): Promise<void> => {
  const token = localStorage.getItem("token");

  const response = await fetch(
    `${API_URL}/profile/references/${numero}/photo`,
    {
      method: "GET",
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Erreur lors du téléchargement: ${response.status}`);
  }

  // Créer un blob et le télécharger
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Retourne l'URL de la photo (pour affichage, nécessite token)
 * @deprecated Utiliser downloadReferencePhoto pour le téléchargement
 */
export const getReferencePhotoUrl = (numero: number): string => {
  return `${API_URL}/profile/references/${numero}/photo`;
};

export default {
  getProfile,
  getProfileCompletion,
  updatePresentation,
  updateCoordonnees,
  updateBancaire,
  updateLots,
  updateLotsForLotTechnique,
  getReferences,
  updateReferences,
  uploadReferencePhoto,
  deleteReferencePhoto,
  downloadReferencePhoto,
  getReferencePhotoUrl,
};
